import { Fragment } from 'react';

const styles = {
  heatmapGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(7, 1fr)',
    gap: '4px',
    alignItems: 'center',
    marginTop: '8px',
  },
  heatmapCorner: {},
  heatmapDayLabel: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: '#52514e',
    fontWeight: 600,
  },
  heatmapRowLabel: {
    fontSize: '0.75rem',
    color: '#52514e',
    whiteSpace: 'nowrap',
    paddingRight: '4px',
  },
  heatmapCell: {
    width: '100%',
    aspectRatio: '1',
    minWidth: '18px',
    borderRadius: '4px',
  },
  heatmapLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '10px',
    fontSize: '0.75rem',
    color: '#898781',
  },
  heatmapLegendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
  },
};

export const DAY_LABELS = {
  day_monday: '月',
  day_tuesday: '火',
  day_wednesday: '水',
  day_thursday: '木',
  day_friday: '金',
  day_saturday: '土',
  day_sunday: '日',
};

export const DAY_ORDER = [
  'day_monday',
  'day_tuesday',
  'day_wednesday',
  'day_thursday',
  'day_friday',
  'day_saturday',
  'day_sunday',
];

// Date#getDay() returns 0(日)〜6(土)
const DAY_KEY_BY_JS_INDEX = [
  'day_sunday',
  'day_monday',
  'day_tuesday',
  'day_wednesday',
  'day_thursday',
  'day_friday',
  'day_saturday',
];

const TIME_BUCKETS = [
  { key: 'stream_time_midnight', label: '深夜', minHour: 0, maxHour: 5 },
  { key: 'stream_time_early_morning', label: '早朝', minHour: 5, maxHour: 8 },
  { key: 'stream_time_morning', label: '朝', minHour: 8, maxHour: 12 },
  { key: 'stream_time_afternoon', label: '昼', minHour: 12, maxHour: 17 },
  { key: 'stream_time_evening', label: '夕方', minHour: 17, maxHour: 20 },
  { key: 'stream_time_night', label: '夜', minHour: 20, maxHour: 24 },
];

const HEATMAP_RAMP = ['#cde2fb', '#86b6ef', '#3987e5', '#1c5cab', '#0d366b'];

const cellKey = (day, bucketKey) => `${day}_${bucketKey}`;

const buildHeatmapCounts = (videos) => {
  const counts = {};

  DAY_ORDER.forEach((day) => {
    TIME_BUCKETS.forEach((bucket) => {
      counts[cellKey(day, bucket.key)] = 0;
    });
  });

  videos.forEach((video) => {
    const startedAt = new Date(video.startedAt);
    const day = DAY_KEY_BY_JS_INDEX[startedAt.getDay()];
    const hour = startedAt.getHours();
    const bucket = TIME_BUCKETS.find(
      ({ minHour, maxHour }) => hour >= minHour && hour < maxHour
    );

    if (bucket) {
      counts[cellKey(day, bucket.key)] += 1;
    }
  });

  return counts;
};

const heatmapCellColor = (count, maxCount) => {
  if (count === 0 || maxCount === 0) {
    return HEATMAP_RAMP[0];
  }

  const ratio = count / maxCount;
  const stepIndex = Math.min(
    HEATMAP_RAMP.length - 1,
    Math.max(1, Math.ceil(ratio * (HEATMAP_RAMP.length - 1)))
  );

  return HEATMAP_RAMP[stepIndex];
};

function StreamHeatmap({ videos }) {
  const counts = buildHeatmapCounts(videos);
  const maxCount = Math.max(0, ...Object.values(counts));

  return (
    <>
      <div style={styles.heatmapGrid}>
        <div style={styles.heatmapCorner} />
        {DAY_ORDER.map((day) => (
          <div key={day} style={styles.heatmapDayLabel}>
            {DAY_LABELS[day]}
          </div>
        ))}

        {TIME_BUCKETS.map((bucket) => (
          <Fragment key={bucket.key}>
            <div style={styles.heatmapRowLabel}>{bucket.label}</div>
            {DAY_ORDER.map((day) => {
              const count = counts[cellKey(day, bucket.key)];
              return (
                <div
                  key={`${bucket.key}_${day}`}
                  title={`${DAY_LABELS[day]}曜・${bucket.label}: ${count}件`}
                  style={{
                    ...styles.heatmapCell,
                    background: heatmapCellColor(count, maxCount),
                  }}
                />
              );
            })}
          </Fragment>
        ))}
      </div>

      <div style={styles.heatmapLegend}>
        <span>少</span>
        {HEATMAP_RAMP.map((color) => (
          <span
            key={color}
            style={{ ...styles.heatmapLegendSwatch, background: color }}
          />
        ))}
        <span>多</span>
      </div>
    </>
  );
}

export default StreamHeatmap;
